import { useSearchParams, usePathname } from 'next/navigation';
import { useSelectedMatterId } from '@/lib/stores/matter-estate-admin-store';

/**
 * Hook to get the current matter ID from multiple sources.
 *
 * Priority order:
 * 1. Zustand store (primary - set by panel)
 * 2. URL search params ?matter=X (fallback - for deep links)
 * 3. URL path /matters/[id] (fallback - for detail pages)
 * 4. Returns 0 if no source has a valid matter ID
 *
 * NOTE: The multiple URL patterns (?matter=X and /matters/[id]) are temporary.
 * These will be migrated to a single standardised pattern in the next phase.
 */
export function useMatterId(): number {
  const selectedMatterId = useSelectedMatterId();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Primary: Use Zustand store if available
  if (selectedMatterId) {
    return selectedMatterId;
  }

  // Fallback 1: Try to read from URL search params (?matter=X)
  const matterParam = searchParams.get('matter');
  if (matterParam) {
    const matterId = parseInt(matterParam, 10);
    if (!isNaN(matterId) && matterId > 0) {
      return matterId;
    }
  }

  // Fallback 2: Try to extract from /matters/[id] path
  const detailsMatch = pathname.match(/^\/matters\/(\d+)/);
  if (detailsMatch) {
    const matterId = parseInt(detailsMatch[1], 10);
    if (!isNaN(matterId) && matterId > 0) {
      return matterId;
    }
  }

  // No valid matter ID found
  return 0;
}
